import { ComponentType, ConnectionPositionPair } from '@angular/cdk/overlay';
import {
  ComponentPortal,
  PortalInjector,
  TemplatePortal
} from '@angular/cdk/portal';
import {
  Injectable,
  InjectionToken,
  Injector,
  TemplateRef,
  ElementRef
} from '@angular/core';
import { ScDynamicOverlay } from './sc-overlay-container';
import { ScPopoverConfig } from './sc-popover-config';
import { ScPopoverRef } from './sc-popover-ref';
import { ScPopoverComponent } from './sc-popover.component';

export const POPOVER_DATA = new InjectionToken('popover.data');

const defaultConfig: ScPopoverConfig = {
  hasBackDrop: true,
  backdropClass: '',
  disableClose: false,
  panelClass: '',
  arrowOffset: 1,
  arrowSize: 1
};

@Injectable()
export class ScPopoverService {
  constructor(private overlay: ScDynamicOverlay, private injector: Injector) {}

  open<D = any>(
    componentOrTemplate: ComponentType<any> | TemplateRef<any>,
    target: ElementRef | HTMLElement,
    config: Partial<ScPopoverConfig> = {},
    overlayContainer?: HTMLElement
  ): ScPopoverRef<D> {
    const popoverConfig: ScPopoverConfig = {
      ...defaultConfig,
      ...config
    };

    const arrowSize = popoverConfig.arrowSize;
    const arrowOffset = popoverConfig.arrowOffset;
    const panelOffset = arrowSize / 2;

    // preferred positions, in order of priority
    const positions: ConnectionPositionPair[] = [
      // bottom center
      {
        overlayX: 'center',
        overlayY: 'top',
        originX: 'center',
        originY: 'bottom',
        panelClass: ['top', 'center'],
        offsetY: panelOffset
      },
      // top center
      {
        overlayX: 'center',
        overlayY: 'bottom',
        originX: 'center',
        originY: 'top',
        panelClass: ['bottom', 'center'],
        offsetY: -1 * panelOffset
      },
      // top left
      {
        overlayX: 'start',
        overlayY: 'bottom',
        originX: 'center',
        originY: 'top',
        panelClass: ['bottom', 'left'],
        offsetX: -1 * arrowOffset,
        offsetY: -1 * panelOffset
      },
      // top right
      {
        overlayX: 'end',
        overlayY: 'bottom',
        originX: 'center',
        originY: 'top',
        panelClass: ['bottom', 'right'],
        offsetX: arrowOffset,
        offsetY: -1 * panelOffset
      },

      // bottom left
      {
        overlayX: 'start',
        overlayY: 'top',
        originX: 'center',
        originY: 'bottom',
        panelClass: ['top', 'left'],
        offsetX: -1 * arrowOffset,
        offsetY: panelOffset
      },
      // bottom right
      {
        overlayX: 'end',
        overlayY: 'top',
        originX: 'center',
        originY: 'bottom',
        panelClass: ['top', 'right'],
        offsetX: arrowOffset,
        offsetY: panelOffset
      }
    ];

    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(target)
      .withPush(true)
      .withFlexibleDimensions(false)
      .withPositions(positions);

    // if (overlayContainer) {
    //     this.overlay.setContainerElement(overlayContainer);
    // }

    const overlayRef = this.overlay.create({
      hasBackdrop: config.hasBackDrop,
      backdropClass: config.backdropClass,
      panelClass: config.panelClass,
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.reposition({
        autoClose: true
      })
    });

    const popoverRef = new ScPopoverRef(
      overlayRef,
      positionStrategy,
      popoverConfig
    );

    const popover = overlayRef.attach(
      new ComponentPortal(
        ScPopoverComponent,
        null,
        new PortalInjector(
          this.injector,
          new WeakMap<any, any>([[ScPopoverRef, popoverRef]])
        )
      )
    ).instance;

    if (componentOrTemplate instanceof TemplateRef) {
      // rendering a provided template dynamically
      popover.attachTemplatePortal(
        new TemplatePortal(componentOrTemplate, null, {
          $implicit: config.data,
          popover: popoverRef
        })
      );
    } else {
      // rendering a provided component dynamically
      popover.attachComponentPortal(
        new ComponentPortal(
          componentOrTemplate,
          null,
          new PortalInjector(
            this.injector,
            new WeakMap<any, any>([
              [POPOVER_DATA, config.data],
              [ScPopoverRef, popoverRef]
            ])
          )
        )
      );
    }

    return popoverRef;
  }
}
